import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { onboardingService, OnboardingProgress, OnboardingUserType, AnyChecklistItemId } from '@/services/onboarding.service';
import { toast } from '@/utils/toast';

/**
 * Onboarding Hooks
 *
 * React Query hooks for onboarding state management
 * - Welcome wizard state
 * - Checklist progress
 * - Tooltip tracking
 */

const QUERY_KEY = ['onboarding'] as const;

/**
 * useOnboardingProgress Hook
 *
 * Fetch user's onboarding progress (wizard, checklist, tooltips)
 * @param enabled - Only fetch when true (use to gate on authentication)
 */
export function useOnboardingProgress(enabled: boolean = true) {
  return useQuery<OnboardingProgress>({
    queryKey: QUERY_KEY,
    queryFn: onboardingService.getProgress,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true, // Pick up backend auto-completions when user returns to the tab
    enabled, // Only fetch when authenticated
  });
}

/**
 * useCompleteWizard Hook
 *
 * Mark welcome wizard as completed
 */
export function useCompleteWizard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onboardingService.completeWizard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useSkipWizard Hook
 *
 * Skip the welcome wizard
 */
export function useSkipWizard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onboardingService.skipWizard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useUpdateWizardStep Hook
 *
 * Update current wizard step
 */
export function useUpdateWizardStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (step: number) => onboardingService.updateWizardStep(step),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useSetTeamType Hook
 *
 * Set team type (individual, team, agency)
 */
export function useSetTeamType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamType: OnboardingUserType) => onboardingService.setTeamType(teamType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useCompleteChecklistItem Hook
 *
 * Mark checklist item as completed
 */
export function useCompleteChecklistItem(options: { silent?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: (item: AnyChecklistItemId) => onboardingService.completeChecklistItem(item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      // Fire-and-forget callers (page-visit auto-ticks) pass silent: true.
      if (!options.silent) toast.error(t('error_generic'));
    },
  });
}

/**
 * useDismissChecklist Hook
 *
 * Dismiss the onboarding checklist
 */
export function useDismissChecklist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onboardingService.dismissChecklist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useMarkTooltipSeen Hook
 *
 * Mark tooltip as seen
 */
export function useMarkTooltipSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tooltipId: string) => onboardingService.markTooltipSeen(tooltipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * useDisableTooltips Hook
 *
 * Disable all tooltips
 */
export function useDisableTooltips() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onboardingService.disableTooltips,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
